import { forwardRef, useEffect, useRef } from "react";
import { SlideEyebrow, SlideHeading } from "../common.tsx";
import { IconStructure, IconShieldCheck, IconAlignLeft, IconUserControl, IconStar } from "../icons.tsx";
import type { SVGProps } from "react";

const DIFF_ICONS: Array<(p: SVGProps<SVGSVGElement>) => JSX.Element> = [
  IconStructure,
  IconShieldCheck,
  IconAlignLeft,
  IconUserControl,
];

interface Props {
  index: number;
  differentiators: string[];
}

export const DifferentiatorsSlide = forwardRef<HTMLElement, Props>(function DifferentiatorsSlide(
  { index, differentiators },
  ref
) {
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const cards = listRef.current?.querySelectorAll<HTMLElement>(".dz-diff");
    if (!cards?.length) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) {
          (e.target as HTMLElement).classList.add("dz-diff--visible");
          io.unobserve(e.target);
        }
      }),
      { threshold: 0.15, rootMargin: "0px 0px -40px 0px" }
    );
    cards.forEach((c) => io.observe(c));
    return () => io.disconnect();
  }, [differentiators.length]);

  return (
    <section ref={ref} className="dz-slide">
      <div className="dz-container">
        <SlideEyebrow index={index} label="Why this beats the status quo" />
        <SlideHeading plain="Differentiators." />
        <div className="dz-diff-list" ref={listRef}>
          {differentiators.map((x, i) => {
            const Icon = DIFF_ICONS[i] ?? IconStar;
            return (
              <div
                className="dz-diff"
                key={i}
                style={{ "--dz-diff-delay": `${i * 0.1}s` } as React.CSSProperties}
              >
                <div className="dz-diff__icon">
                  <Icon />
                </div>
                <p>{x}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
});

